import { useCallback, useEffect, useState } from 'react';

const readSearchParam = (key: string): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  return params.get(key) ?? null;
};

/**
 * Keeps a single URL search param in sync with component state.
 */
export const useUrlSearchParam = (key: string): readonly [string | null, (value: string | null) => void] => {
  const [value, setValue] = useState<string | null>(() => readSearchParam(key));

  useEffect(() => {
    const handlePopState = (): void => {
      setValue(readSearchParam(key));
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [key]);

  const update = useCallback(
    (nextValue: string | null): void => {
      setValue(nextValue);

      if (typeof window === 'undefined') {
        return;
      }

      const url = new URL(window.location.href);
      if (nextValue == null) {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, nextValue);
      }

      window.history.pushState({}, '', url.toString());
    },
    [key],
  );

  return [value, update] as const;
};
